import React, { useEffect, useContext } from 'react';
import fetcher from 'utils/fetcher';
import cQuery from 'endpoints/Collection/col.gql';
import FullTeaser from 'components/FullTeaser/FullTeaser';
import { DefaultCollection } from 'models/Collection/CollectionType';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import '/public/css/components/scrollmenu.css';
import '/public/css/pages/talkingSense.css';
import '/public/css/components/fancySlider.css';
import { Image } from '@apmg/mimas';
import Link from 'components/AppSafeLink/AppSafeLink';
import { linkByTypeAs } from 'utils/cjsutils';
import AudioPlayButton from 'components/AudioPlayButton/AudioPlayButton';
import { audioDownloadPrefix } from 'utils/utils';
import ContentGrid from 'grids/ContentGrid';
import Sidebar from 'components/Sidebar/Sidebar';
import { getServerSession } from 'next-auth';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import type { GetServerSideProps } from 'next';
import { Body } from '@apmg/amat';
import Collection from 'endpoints/Collection/Collection';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { LeftArrow } from 'components/Slider/arrows';
import { RightArrow } from 'components/Slider/arrows';
import AdvertisementBanner from 'components/Advertisement/AdvertisementBanner';
import AdCategoriesContext from 'context/AdCategoriesContext';
import Metatags from 'components/Metatags/Metatags';

interface Props {
  results: {
    collection: DefaultCollection;
  };
  videos: {
    collection: DefaultCollection;
  };
}

const TalkingSense = (props: Props) => {
  const context = useContext(AdCategoriesContext);
  const collection = props.results.collection;

  const heroFirstItem = collection.results.items[0];

  const BodySidebar = props?.results?.collection?.body ? (
    <div className="section-sm talking-sense-side-text">
      <Body nodeData={JSON.parse(props?.results?.collection?.body)} />
    </div>
  ) : (
    <></>
  );
  const lowerItems = {
    collection: {
      results: {
        items: collection.results.items.slice(6),
        currentPage: collection.results.currentPage,
        totalPages: collection.results.totalPages,
      },
      canonicalSlug: 'talking-sense',
    },
  };

  useEffect(() => {
    if (context) {
      context.setPrimaryCategory('talking-sense');
      context.setAdCategories(['talking-sense']);
    }
  }, []);

  return (
    <>
      <Metatags fullSlug="talking-sense" title="Talking Sense" />
      <div className="talking-sense-outer">
        <div className="talking-sense-logo-container container">
          <h1 className="talking-sense-logo">
            <img src="/img/talking-sense-wordmark.svg" alt="Talking Sense" />
          </h1>
          <img
            src="/img/MPR-news-logo-white.svg"
            alt="MPR News"
            className="talking-sense-mpr-logo"
          />
          <div className="premium-underwriting">
            <AdvertisementBanner
              id="premium_ad"
              sizes={[[320, 25]]}
              sizeMap={{ 0: [320, 25] }}
              customCss="underwriting"
              primaryCollection="talking-sense"
            />
          </div>
        </div>
        <ContentGrid
          className="talking-sense-contentgrid"
          sidebar={
            <Sidebar
              pageTopic="Talking Sense"
              newsletter={false}
              CustomContent={
                props?.results?.collection?.body ? BodySidebar : <></>
              }
            />
          }
          mainClassName=""
        >
          <div id="mainContent"></div>
          <div className="collection talking-sense">
            <div className="story-content">
              <div className="grid grid-2col">
                <div className="talking-sense-main-story">
                  <FullTeaser
                    item={heroFirstItem}
                    hideAddFavorites={true}
                    size="normal"
                    newspartners={false}
                    loading="lazy"
                    condensedBtns={false}
                    saveButtonBG={false}
                  />
                </div>
                <div className="talking-sense-more-stories">
                  {collection.results.items.slice(1, 6).map((item) => {
                    return (
                      <div
                        className="talking-sense-story"
                        key={item.canonicalSlug}
                      >
                        {item.primaryVisuals?.thumbnail?.aspect_ratios
                          ?.thumbnail && (
                          <div className="talking-sense-story-img">
                            <Link
                              href={linkByTypeAs(item)}
                              key={item.canonicalSlug}
                              passHref={true}
                            >
                              <Image
                                image={item.primaryVisuals.thumbnail}
                                aspectRatio="thumbnail"
                                sizes="(min-width: 72em) 10vw,  80vw"
                              />
                            </Link>
                          </div>
                        )}
                        <div>
                          <Link
                            href={linkByTypeAs(item)}
                            key={item.canonicalSlug}
                            passHref={true}
                          >
                            <h2 className="type-lg">{item.title}</h2>
                          </Link>
                          {item?.audio?.[0]?.encodings?.[0]?.playFilePath && (
                            <AudioPlayButton
                              audioTitle={item.title}
                              label="Listen"
                              audioSource={audioDownloadPrefix(
                                item.audio[0].encodings[0].playFilePath
                              )}
                              showDuration={`${
                                item.audio[0].encodings[0].durationHms &&
                                item.audio[0].encodings[0].durationHms
                              }`}
                              playBtnSm={true}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="talking-sense-promo">
              <div>
                <h2 className="talking-sense-promo-heading">
                  Try the Talking Sense digital tool to start having hard
                  political conversations better.
                </h2>
              </div>
              <a
                href="https://talkingsense.mprnews.org/"
                className="btn btn-white btn-semi-slim type-lg"
              >
                Try It Now
              </a>
            </div>
          </div>
        </ContentGrid>
        <div className="container story-content talking-sense-events">
          <h2 className="type-xl type-mb">{props.videos.collection.title}</h2>
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow}>
            {props.videos?.collection.results?.items.map((item, idx) => {
              return (
                <FullTeaser
                  key={item.id + idx}
                  item={item}
                  headingLvl={3}
                  hideAddFavorites={true}
                  condensedBtns={true}
                />
              );
            })}
          </ScrollMenu>
        </div>

        <div className="container story-content talking-sense-lower-stories">
          <h2 className="hdg hdg-2">More stories from {collection.title}</h2>
          <Collection data={lowerItems} withinPage />
        </div>
        <div className="talking-sense-video-container">
          <h2
            className="talking-sense-slider-heading type-3x"
            id="communication-styles"
          >
            Communication Styles
          </h2>
          <p className="talking-sense-slider-desc">
            Your next hard conversation will go better if you prepare. It helps
            to know who you&rsquo;re talking to and how they will react. Here
            are four communication archetypes you might encounter in your next
            challenging political conversation.
          </p>
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow}>
            <div>
              <h3>The Gladiator</h3>
              <video
                poster="https://talkingsense.mprnews.org/images/talking-sense-gladiator.jpg"
                src="https://player.vimeo.com/progressive_redirect/playback/906146868/rendition/1080p/file.mp4?loc=external&amp;signature=af409d598d02d591ac06ca70abce15e45eca1086038966e90990faa41a4b9ba0"
                controls={true}
              ></video>
            </div>
            <div>
              <h3>The Defender</h3>
              <video
                poster="https://talkingsense.mprnews.org/images/talking-sense-defender.jpg"
                src="https://player.vimeo.com/progressive_redirect/playback/906146980/rendition/1080p/file.mp4?loc=external&amp;signature=86797d916abd1d8162f28c57a3cf620fb92992babf45d624bf9c5275a8ce802d"
                controls={true}
              ></video>
            </div>
            <div>
              <h3>The Peacekeeper</h3>
              <video
                poster="https://talkingsense.mprnews.org/images/talking-sense-peacekeeper.jpg"
                src="https://player.vimeo.com/progressive_redirect/playback/906146722/rendition/720p/file.mp4?loc=external&amp;signature=450f87b80d52b671d20623a1cab2010b6d7c5859e8b9f4ffda272c5ee7aed2c3"
                controls={true}
              ></video>
            </div>
            <div>
              <h3>The Sniper</h3>
              <video
                poster="https://talkingsense.mprnews.org/images/talking-sense-sniper.jpg"
                src="https://player.vimeo.com/progressive_redirect/playback/906146614/rendition/720p/file.mp4?loc=external&amp;signature=983cda1bd4f79d0cc02e7e13d6ac22e52da2d5a8b0e56004ce40eaa8b9feea25"
                controls={true}
              ></video>
            </div>
          </ScrollMenu>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps = (async (context) => {
  const [results, videos] = await Promise.all([
    fetcher(cQuery, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: 'talking-sense',
    }),
    fetcher(cQuery, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: 'talking-sense-videos',
    }),
  ]);

  const session = await getServerSession(context.req, context.res, authOptions);
  return { props: { results, videos, session } };
}) satisfies GetServerSideProps;

export default TalkingSense;
